import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useCreateDecision,
  useDecisions,
  useDecisionsAwaitingReview,
  useDecisionSummary,
  useDeleteDecision,
  useRecordOutcome,
  useTeams,
  useUpdateDecision,
  type DecisionRow,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate } from '@/lib/format';
import { todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';

const CATEGORIES = ['load', 'selection', 'planning', 'player', 'staff', 'other'] as const;
const VERDICTS = ['worked', 'mixed', 'did_not_work'] as const;

const verdictTone: Record<string, string> = {
  worked: 'text-ok',
  mixed: 'text-ink-muted',
  did_not_work: 'text-risk',
};

/**
 * The decision log.
 *
 * Most of this product records decisions as a property of the thing decided.
 * This page is for the rest — and its real feature is the review list, because
 * an outcome field with nothing prompting it is a field nobody fills in.
 */
export function Decisions({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const [teamId, setTeamId] = useState<string | null>(null);
  const teamList = teams.data ?? [];
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const list = useDecisions({ teamId: activeTeamId ?? undefined });
  const review = useDecisionsAwaitingReview();
  const summary = useDecisionSummary(activeTeamId ?? undefined);
  const create = useCreateDecision();
  const update = useUpdateDecision();
  const outcome = useRecordOutcome();
  const remove = useDeleteDecision();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [onDate, setOnDate] = useState(todayISO());
  const [category, setCategory] = useState('other');
  const [what, setWhat] = useState('');
  const [why, setWhy] = useState('');

  const [outcomeFor, setOutcomeFor] = useState<string | null>(null);
  const [outcomeText, setOutcomeText] = useState('');
  const [verdict, setVerdict] = useState('');

  const startCreate = () => {
    setEditingId(null);
    setOnDate(todayISO());
    setCategory('other');
    setWhat('');
    setWhy('');
    setOpen(true);
  };

  const startEdit = (row: DecisionRow) => {
    setEditingId(row.id);
    setOnDate(row.onDate);
    setCategory(row.category);
    setWhat(row.decision);
    setWhy(row.reason);
    setOpen(true);
  };

  const save = async () => {
    const input = {
      teamId: activeTeamId,
      onDate,
      category,
      decision: what,
      reason: why,
    };
    const ok = await submit(async () => {
      if (editingId) await update.mutateAsync({ id: editingId, input });
      else await create.mutateAsync(input);
    });
    if (ok) {
      setOpen(false);
      setEditingId(null);
    }
  };

  const rows = list.data ?? [];
  const pending = review.data ?? [];

  return (
    <>
      <PageHeader
        title={t('decisions.title')}
        count={
          summary.data
            ? t('decisions.reviewed', {
                count: summary.data.reviewed,
                total: summary.data.total,
              })
            : rows.length || undefined
        }
        lead={t('decisions.lead')}
        help={localePath(locale, '/guide/reports')}
        action={
          // Not while the list is empty: the empty state offers the same
          // action, in the middle of the screen where the eye already is, and
          // two identical primary buttons four hundred pixels apart is a
          // screen that looks generated rather than designed.
          open || rows.length === 0 ? null : (
            <Button variant="primary" onClick={startCreate}>
              {t('decisions.add')}
            </Button>
          )
        }
      />

      {teamList.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {teamList.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => setTeamId(tm.id)}
              aria-pressed={tm.id === activeTeamId}
              className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                tm.id === activeTeamId
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
              }`}
            >
              {tm.name}
            </button>
          ))}
        </div>
      ) : null}

      <div className="grid gap-5">
        {open ? (
          <Panel
            title={editingId ? t('decisions.edit') : t('decisions.add')}
            action={
              <Button variant="quiet" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
            }
          >
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}
              <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-4">
                <Field label={t('decisions.date')} error={fieldError('onDate')}>
                  {(p) => (
                    <TextInput
                      {...p}
                      type="date"
                      value={onDate}
                      onChange={(e) => setOnDate(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Field label={t('decisions.category')}>
                  {(p) => (
                    <SelectInput
                      {...p}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {t(`decisions.categoryValue.${c}`)}
                        </option>
                      ))}
                    </SelectInput>
                  )}
                </Field>
                <div className="sm:col-span-2">
                  <Field
                    label={t('decisions.what')}
                    hint={t('decisions.whatHint')}
                    error={fieldError('decision')}
                  >
                    {(p) => (
                      <TextInput
                        {...p}
                        value={what}
                        onChange={(e) => setWhat(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                </div>
                <div className="sm:col-span-4">
                  <Field
                    label={t('decisions.why')}
                    hint={t('decisions.whyHint')}
                    error={fieldError('reason')}
                  >
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={2}
                        value={why}
                        onChange={(e) => setWhy(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-4">
                <Button
                  type="submit"
                  variant="primary"
                  busy={busy}
                  disabled={!what.trim() || !why.trim()}
                >
                  {t('decisions.save')}
                </Button>
                {!why.trim() && what.trim() ? (
                  <span className="text-meta text-ink-faint">{t('decisions.needsReason')}</span>
                ) : null}
              </div>
            </form>
          </Panel>
        ) : null}

        {pending.length > 0 ? (
          <Panel title={t('decisions.awaiting')} count={pending.length}>
            <p className="px-4 pt-3 text-meta text-ink-muted">{t('decisions.awaitingBody')}</p>
            <ul className="divide-y divide-line">
              {pending.map((row) => (
                <li key={row.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="tabular text-meta text-ink-faint">
                      {formatDate(row.onDate, locale)}
                    </span>
                    <span className="font-medium">{row.decision}</span>
                  </div>
                  {outcomeFor === row.id ? (
                    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
                      <Field label={t('decisions.outcome')}>
                        {(p) => (
                          <TextInput
                            {...p}
                            value={outcomeText}
                            onChange={(e) => setOutcomeText(e.target.value)}
                          />
                        )}
                      </Field>
                      <Field label={t('decisions.verdict.unset')}>
                        {(p) => (
                          <SelectInput
                            {...p}
                            value={verdict}
                            onChange={(e) => setVerdict(e.target.value)}
                          >
                            <option value="">{t('decisions.verdict.unset')}</option>
                            {VERDICTS.map((v) => (
                              <option key={v} value={v}>
                                {t(`decisions.verdict.${v}`)}
                              </option>
                            ))}
                          </SelectInput>
                        )}
                      </Field>
                      <div className="pb-2">
                        <Button
                          variant="primary"
                          busy={outcome.isPending}
                          disabled={!outcomeText.trim()}
                          onClick={() =>
                            void outcome
                              .mutateAsync({
                                id: row.id,
                                input: {
                                  outcome: outcomeText,
                                  outcomeVerdict: verdict || null,
                                },
                              })
                              .then(() => {
                                setOutcomeFor(null);
                                setOutcomeText('');
                                setVerdict('');
                              })
                          }
                        >
                          {t('common.save')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setOutcomeFor(row.id);
                        setOutcomeText('');
                        setVerdict('');
                      }}
                    >
                      {t('decisions.recordOutcome')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        <Panel>
          {list.isLoading ? (
            <p className="flex items-center gap-2 px-4 py-6 text-ink-muted">
              <Spinner /> {t('common.loading')}
            </p>
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('decisions.empty')}
              body={t('decisions.emptyBody')}
              action={
                <Button variant="primary" onClick={startCreate}>
                  {t('decisions.add')}
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((row) => (
                <li key={row.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                      {formatDate(row.onDate, locale)}
                    </span>
                    <span className="font-medium">{row.decision}</span>
                    <span className="text-meta text-ink-faint">
                      {t(`decisions.categoryValue.${row.category}`)}
                    </span>
                    <span className="ms-auto flex items-center gap-2">
                      {row.outcomeVerdict ? (
                        <span className={`text-meta ${verdictTone[row.outcomeVerdict] ?? ''}`}>
                          {t(`decisions.verdict.${row.outcomeVerdict}`)}
                        </span>
                      ) : null}
                      <Button variant="quiet" onClick={() => startEdit(row)}>
                        {t('common.edit')}
                      </Button>
                    </span>
                  </div>
                  <p className="mt-1 max-w-[80ch] text-meta text-ink-muted">{row.reason}</p>
                  {row.outcome ? (
                    <p className="mt-1 max-w-[80ch] text-meta">
                      <span className="text-ink-faint">
                        {t('decisions.outcome')}
                        {row.outcomeOn ? ` · ${formatDate(row.outcomeOn, locale)}` : ''}:{' '}
                      </span>
                      {row.outcome}
                    </p>
                  ) : (
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setOutcomeFor(row.id);
                        setOutcomeText('');
                        setVerdict('');
                      }}
                    >
                      {t('decisions.recordOutcome')}
                    </Button>
                  )}
                  {outcomeFor === row.id && !row.outcome ? (
                    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
                      <Field label={t('decisions.outcome')} hint={t('decisions.outcomeHint')}>
                        {(p) => (
                          <TextInput
                            {...p}
                            value={outcomeText}
                            onChange={(e) => setOutcomeText(e.target.value)}
                          />
                        )}
                      </Field>
                      <Field label={t('decisions.verdict.unset')}>
                        {(p) => (
                          <SelectInput
                            {...p}
                            value={verdict}
                            onChange={(e) => setVerdict(e.target.value)}
                          >
                            <option value="">{t('decisions.verdict.unset')}</option>
                            {VERDICTS.map((v) => (
                              <option key={v} value={v}>
                                {t(`decisions.verdict.${v}`)}
                              </option>
                            ))}
                          </SelectInput>
                        )}
                      </Field>
                      <div className="flex gap-2 pb-2">
                        <Button
                          variant="primary"
                          busy={outcome.isPending}
                          disabled={!outcomeText.trim()}
                          onClick={() =>
                            void outcome
                              .mutateAsync({
                                id: row.id,
                                input: { outcome: outcomeText, outcomeVerdict: verdict || null },
                              })
                              .then(() => setOutcomeFor(null))
                          }
                        >
                          {t('common.save')}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (!window.confirm(t('decisions.confirmDelete'))) return;
                            void remove.mutateAsync(row.id).then(() => setOutcomeFor(null));
                          }}
                        >
                          {t('decisions.delete')}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <p className="border-t border-line px-4 py-3 text-meta text-ink-faint">
            {t('decisions.mirrorNote')}
          </p>
        </Panel>
      </div>
    </>
  );
}
